import { getDeleteApiGroupIdMutationOptions, type GroupViewDTO } from '@smartsash/api-client';
import { useEntityDelete } from '@smartsash/server-ops';
import { Avatar, Badge, Icon, openModal } from '@smartsash/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './Groups.module.css';

import { useInstitutionNames, useLabCountByGroupId } from '@/hooks/useLookupNames';
import { ADMIN_LIST_SEARCH_DEFAULTS } from '@/router/listSearch';
import { DEVICES_GROUP_FIELD, devicesFilteredBy } from '@/ui/pages/Devices/devicesModel';
import { USERS_GROUP_FIELD, usersFilteredBy } from '@/ui/pages/Users/usersModel';
import { MobileCardActions, type RowActionEntry } from '@/ui/shared/RowActions';

function MobileCard({ row }: { row: GroupViewDTO }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // HS-869: the card reads the DTO the list query returns, so the institution name is joined
  // here (the same cached lookup the table's `meta` uses) rather than by a page-level mapper.
  const institutionNames = useInstitutionNames();
  const labCountByGroupId = useLabCountByGroupId();
  const id = row.id ?? '';
  const institution =
    row.institutionId == null ? '' : (institutionNames.get(row.institutionId) ?? '');
  const labCount = id === '' ? 0 : (labCountByGroupId.get(id) ?? 0);
  // HS-847: delete via server-ops (invalidates the group list query + toast).
  const del = useEntityDelete({
    entity: 'group',
    mutationOptions: getDeleteApiGroupIdMutationOptions,
    // HS-1141: the deleted record's own reads are dropped, not refetched into a 404.
    deletesRecord: true,
  });

  // HS-1177: the imperative modal replaces a `useState` pair plus a conditionally
  // rendered dialog. It resolves with the pressed action's `value`, or `null` when
  // dismissed via the overlay, Escape or the close button.
  const handleDelete = useCallback(async () => {
    const { action } = await openModal({
      title: t('groups.deleteTitle', { name: row.name }),
      description: t('groups.deleteDescription'),
      variant: 'error',
      icon: <Icon name="trash-01" />,
      actions: [
        { label: t('common.cancel'), value: 'cancel' },
        { label: t('common.delete'), value: 'confirm', destructive: true },
      ],
    }).closed;

    if (action === 'confirm') del.mutate({ id });
  }, [del, id, row.name, t]);

  // HS-1396: the keys mirror `./RowActions.tsx` entry for entry — see the note there on why the
  // two menus have to be kept in step by hand, and `../rowActionParity.test.ts` for the guard.
  const items: RowActionEntry[] = [
    {
      label: t('common.edit'),
      icon: 'edit-01',
      permission: 'group.edit',
      onClick: () => {
        void navigate({ to: '/groups/$id/edit', params: { id } });
      },
    },
    null,
    {
      label: t('groups.actions.viewLaboratories'),
      icon: 'lab',
      onClick: () => {
        void navigate({
          to: '/laboratories',
          search: { ...ADMIN_LIST_SEARCH_DEFAULTS, groupId: id },
        });
      },
    },
    {
      label: t('groups.actions.viewDevices'),
      icon: 'tv-01',
      onClick: () => {
        // HS-1510: narrows to this group's devices, matching `./RowActions.tsx` — see the note
        // there on why the pre-existing "no group filter" comment was wrong.
        void navigate({ to: '/devices', search: devicesFilteredBy(DEVICES_GROUP_FIELD, id) });
      },
    },
    {
      label: t('groups.actions.viewUsers'),
      icon: 'users-02',
      onClick: () => {
        // HS-1240: `GET /api/User/list` takes the group as a GUID, matched on the user's active
        // assignments — see `usersFilteredBy`.
        void navigate({ to: '/users', search: usersFilteredBy(USERS_GROUP_FIELD, id) });
      },
    },
    null,
    {
      label: t('common.delete'),
      icon: 'trash-01',
      permission: 'group.delete',
      onClick: () => {
        void handleDelete();
      },
    },
  ];

  return (
    <div className={styles['m-card']}>
      <Link to="/groups/$id" params={{ id }} className={styles['m-card-title']}>
        {row.name}
      </Link>
      <div className={styles['m-card-badges']}>
        <Badge variant="modern" size="sm" iconLeading={<Icon name="hash-02" />}>
          {row.accountNumber}
        </Badge>
        <Badge variant="modern" size="sm" iconLeading={<Icon name="bank" />}>
          {institution}
        </Badge>
        <Badge variant="modern" size="sm" iconLeading={<Icon name="marker-pin-02" />}>
          {row.address}
        </Badge>
        <Badge variant="modern" size="sm">
          <span className={styles['m-badge-avatar']}>
            <Avatar name={row.contact ?? ''} size="xs" />
            {row.contact}
          </span>
        </Badge>
        <Badge variant="modern" size="sm" iconLeading={<Icon name="phone" />}>
          {row.phoneNumber}
        </Badge>
        <Badge variant="modern" size="sm" iconLeading={<Icon name="mail-01" />}>
          {row.email}
        </Badge>
        <Badge variant="modern" size="sm" iconLeading={<Icon name="lab" />}>
          {t('groups.mobile.labsCount', { count: labCount })}
        </Badge>
      </div>
      <div className={styles['m-card-meta']}>
        <span className={styles['m-card-meta-label']}>{t('groups.columns.address')}</span>
        <span className={styles['m-card-meta-value']}>{row.address}</span>
      </div>
      <MobileCardActions items={items} />
    </div>
  );
}

export default MobileCard;
